import React, {Component} from "react";
import {Alert, Button, Checkbox, Col, Divider, Form, FormInstance, Input, message, Radio, Row, Tooltip} from "antd";
import {MinusCircleOutlined, PlusOutlined} from '@ant-design/icons';
import {
    LoggingDefine,
    LoggingLevel,
    PredicateDefinition,
    RouteDefinition,
    SecretDefine
} from "../../domains/route-definition";
import {request} from "../../utils/utils";
import TextArea from "antd/es/input/TextArea";
import LeakyBucketRateLimiter from "./leaky-bucket-rate-limiter";
import TokenBucketRateLimiter from "./token-bucket-rate-limiter";

interface RouteEditParam {
    route?: RouteDefinition
    onRouteConfigSuccess: () => void
}

interface RouteEditStatus {
    isEnabledPathRewrite: boolean
    isEnabledFallback: boolean
    isEnabledRetry: boolean
    isEnabledRateLimiter: boolean
    isEnabledAuth: boolean
    isEnabledLogging: boolean
    isEnabledSecretFilter: boolean
    selectedLoggingLevel?: LoggingLevel
    selectedRateLimiter: string
}

export default class RouteEdit extends Component<RouteEditParam, RouteEditStatus> {
    layout = {
        labelCol: {span: 4},
        wrapperCol: {span: 20}
    };
    formRef = React.createRef<FormInstance>()
    lbUri = "lb://cusLB"

    constructor(props: Readonly<RouteEditParam> | RouteEditParam) {
        super(props);
        this.state = {
            isEnabledPathRewrite: false,
            isEnabledFallback: false,
            isEnabledRetry: false,
            isEnabledRateLimiter: false,
            isEnabledAuth: false,
            isEnabledLogging: false,
            isEnabledSecretFilter: false,
            selectedRateLimiter: ""
        }
    }

    componentDidMount() {
        if (this.props.route) {
            this.fillUpForm(this.props.route)
        }
    }

    fillUpForm(entity: RouteDefinition) {
        let dispatchConfig = "";
        let dispatchRule = "";
        let isEnabledPathRewrite = false;
        let isEnabledFallback = false;
        let isEnabledRetry = false;
        let isEnabledRateLimiter = false;
        let selectedRateLimiter = "";
        let targetPath = ""
        let rewriteValue = ""
        let fallbackUri = ""
        let fallbackConfig = "default"
        let retryCount = 3
        let retryStatusCode = ""
        let retryMethod: string[] = []
        let retryMinTime = ""
        let retryMaxTime = ""
        let rateLimiterType = ""
        let rateLimiterMaxRequest = ""
        let rateLimiterAvgRequest = ""
        let host = ""
        let path = ""
        let secret: any[] = []

        entity.predicates.forEach(x => {
            if (x.name.toLowerCase() === "path") {
                path = x.args.pattern
            } else if (x.name.toLowerCase() === "host") {
                host = x.args?.pattern
            }
        })
        entity.filters.forEach(x => {
            if (x.name === "RewritePath") {
                isEnabledPathRewrite = true
                targetPath = x.args._genkey_0
                rewriteValue = x.args._genkey_1
            }
            if (x.name === "CircuitBreaker") {
                isEnabledFallback = true
                fallbackUri = x.args.fallbackUri.replace("forward:", "")
                fallbackConfig = x.args.name
            }
            if (x.name === "Retry") {
                isEnabledRetry = true
                retryCount = x.args.retries
                retryMethod = x.args.methods.split(",")
                retryStatusCode = x.args.statuses
                // let t = JSON.parse(x.args.backoff)
                retryMinTime = x.args["backoff.firstBackoff"].replace("ms", "")
                retryMaxTime = x.args["backoff.maxBackoff"].replace("ms", "")
            }
            if (x.name === "RequestRateLimiter") {
                isEnabledRateLimiter = true
                selectedRateLimiter = "TokenBucket"
                rateLimiterType = x.args.rateLimiterType
                rateLimiterMaxRequest = x.args["redis-rate-limiter.burstCapacity"]
                rateLimiterAvgRequest = x.args["redis-rate-limiter.replenishRate"]
            }
            if (x.name === "LeakyBucketRateLimiter") {
                isEnabledRateLimiter = true
                selectedRateLimiter = "LeakyBucket"
                rateLimiterType = x.args.rateLimiterType
                rateLimiterMaxRequest = x.args["bucketSize"]
                rateLimiterAvgRequest = x.args["leakySpeed"]
            }
        })
        entity.secretDefine?.forEach((item) => {
            secret.push({
                path: item.path,
                filter: item.filters.join("\n")
            })
        })
        this.setState({
            isEnabledPathRewrite: isEnabledPathRewrite,
            isEnabledFallback: isEnabledFallback,
            isEnabledRetry: isEnabledRetry,
            isEnabledRateLimiter: isEnabledRateLimiter,
            isEnabledAuth: entity.authDefine.enable,
            isEnabledLogging: entity.loggingDefine?.enable ?? false,
            isEnabledSecretFilter: !!entity.secretDefine && entity.secretDefine.length > 0,
            selectedRateLimiter: selectedRateLimiter
        })

        let address = entity.uri === this.lbUri ? entity.address : [entity.uri]

        this.formRef.current?.setFieldsValue({
            routeId: entity.id,
            uri: entity.uri,
            dispatchConfig: dispatchConfig,
            dispatchRule: dispatchRule,
            rewriteTarget: targetPath,
            rewriteValue: rewriteValue,
            address: address,
            fallbackUri: fallbackUri,
            fallbackConfig: fallbackConfig,
            retryCount: retryCount,
            retryStatusCode: retryStatusCode,
            retryMethod: retryMethod,
            retryMaxTime: retryMaxTime,
            retryMinTime: retryMinTime,
            rateLimiterType: rateLimiterType,
            maxReqPerSec: rateLimiterMaxRequest,
            avgReqPerSec: rateLimiterAvgRequest,
            loggingLevel: entity.loggingDefine?.level,
            host: host,
            path: path,
            secret: secret,
            rateLimiter: selectedRateLimiter
        })
    }

    submitRouteConfig = () => {
        this.formRef!.current?.submit();
    }

    handleFormSubmit = (values: any) => {
        let predicates: PredicateDefinition[] = []
        let filters: PredicateDefinition[] = []
        let address = []
        let uri = ""

        // 处理服务地址
        if (values.address.length == 0) {
            message.error("你必须提供你的服务地址")
        } else if (values.address.length == 1) {
            uri = values.address[0]
        } else {
            uri = this.lbUri
            address = values.address
        }

        // 处理转发
        if (values.host) {
            let hostDispatch: PredicateDefinition = {
                name: "Host",
                args: {
                    "pattern": values.host
                }
            }
            predicates.push(hostDispatch)
        }
        if (values.path) {
            let pathDispatch: PredicateDefinition = {
                name: "Path",
                args: {
                    "pattern": values.path
                }
            }
            predicates.push(pathDispatch)
        }
        // 处理请求限流
        if (this.state.isEnabledRateLimiter) {
            if(this.state.selectedRateLimiter === "TokenBucket"){
                let rateLimiter = {
                    name: "RequestRateLimiter",
                    args: {
                        "key-resolver": `#{@customKeyResolver}`,
                        "rateLimiterType": 'request.routeId',
                        "redis-rate-limiter.burstCapacity": values.maxReqPerSec,
                        "redis-rate-limiter.replenishRate": values.avgReqPerSec
                    }
                }
                filters.push(rateLimiter)
            } else if (this.state.selectedRateLimiter === "LeakyBucket") {
                let rateLimiter = {
                    name: "LeakyBucketRateLimiter",
                    args: {
                        "rateLimiterType": 'request.routeId',
                        "bucketSize": values.maxReqPerSec,
                        "leakySpeed": values.avgReqPerSec
                    }
                }
                filters.push(rateLimiter)
            }


        }
        // 处理熔断
        if (this.state.isEnabledFallback) {
            if (values.fallbackUri) {
                let fallback = {
                    name: "CircuitBreaker",
                    args: {
                        name: values.fallbackConfig,
                        fallbackUri: "forward:" + values.fallbackUri
                    }
                }
                filters.push(fallback)
            } else {
                message.error("当启用了自定义的熔断配置，你必须提供一个回退转发路径")
            }
        }
        // 处理路径重写
        if (this.state.isEnabledPathRewrite) {
            if (values.rewriteTarget && values.rewriteValue) {
                let pathRewrite = {
                    name: "RewritePath",
                    args: {
                        "_genkey_0": values.rewriteTarget,
                        "_genkey_1": values.rewriteValue
                    }
                }
                filters.push(pathRewrite)
            } else {
                message.error("当启用了路径重写时，你必须提供对应的重写目标和重写值")
                return false;
            }
        }
        // deal with retry
        if (this.state.isEnabledRetry) {
            let retryFilter = {
                name: "Retry",
                args: {
                    retries: values.retryCount,
                    statuses: "BAD_GATEWAY,INTERNAL_SERVER_ERROR,SERVICE_UNAVAILABLE,GATEWAY_TIMEOUT",
                    methods: "",
                    "backoff.firstBackoff": values.retryMinTime + "ms",
                    "backoff.maxBackoff": values.retryMaxTime + "ms",
                }
            }
            if (values.retryStatusCode !== "") {
                retryFilter.args.methods = values.retryMethod
            }
            retryFilter.args.methods = values.retryMethod.join(",")
            filters.push(retryFilter)
        }

        let route: RouteDefinition = {
            id: values.routeId,
            uri: uri,
            predicates: predicates,
            filters: filters,
            order: 0,
            metadata: {},
            address: address,
            enabled: true,
            authDefine: {
                enable: this.state.isEnabledAuth,
                skipPath: values.skipPath
            }
        }
        if (this.state.isEnabledLogging) {
            let logging: LoggingDefine = {
                enable: this.state.isEnabledLogging,
                level: values.loggingLevel,
            }
            if (this.state.selectedLoggingLevel === LoggingLevel.WARNING){
                logging.extensions = {
                    "time": values.requestTime === "" ? 10000 : +values.requestTime
                }
            }
            route.loggingDefine = logging
        }
        if (this.state.isEnabledSecretFilter) {
            let lst: SecretDefine[] = []
            values.secret.forEach((obj:any) => {
                lst.push({
                    path: obj.path,
                    filters: obj.filter.split("\n")
                })
            })
            route.secretDefine = lst
        }
        request({
            method: "POST",
            url: "/gateway/route/add",
            data: route,
            headers: {'ContentType': 'application/json'}
        }).then((res) => {
            if (res.data) {
                this.props.onRouteConfigSuccess()
                this.formRef.current!.resetFields()
                message.info("路由配置成功，请等待配置生效，通常需要30-60s")
                return true;
            } else {
                message.info("路由配置失败，请检查你的路由配置")
                return false;
            }
        }).catch(res => {
            message.info("路由配置失败，请稍后重试" + res)
        })
    }

    clear = () => {
        this.formRef.current!.resetFields()
    }

    private generateSecretConfig = () => {
        return (
            <Form.List name={"secret"}>
                {(fields, {add, remove}) => (
                    <>
                        {fields.map((field, index) => (
                            <>
                                <Form.Item label={"请求路径"} name={[field.name, "path"]}>
                                    <Input />
                                </Form.Item>
                                <Form.Item label={"数据路径"} name={[field.name, "filter"]}>
                                    <TextArea placeholder={"请再次输入要过滤的数据路径，一行一个，例如： “a.b.c[1].d”"} />
                                </Form.Item>
                                <Button type={"dashed"}  onClick={() => remove(field.name)} block icon={<MinusCircleOutlined />}>
                                    删除
                                </Button>
                            </>
                        ))}
                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined/>}>
                            新增配置
                        </Button>
                    </>
                )}
            </Form.List>
        )
    }

    render() {
        return (
            <Form {...this.layout} layout="horizontal" onFinish={this.handleFormSubmit} ref={this.formRef}>
                <Form.Item label="路由ID" name="routeId" rules={[{required: true, message: "你必须提供一个路由id"}]}>
                    <Input/>
                </Form.Item>
                <Divider/>
                <Form.Item label="服务地址">
                    <Form.List name="address">
                        {(fields, {add, remove}) => (
                            <>
                                {fields.map((field, index) => (

                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        marginBottom: '24px'
                                    }}>
                                        <Form.Item {...field} style={{
                                            margin: "0 5px 0 0",
                                            width: '100%'
                                        }}>
                                            <Input style={{marginRight: 5}} placeholder={"请输入服务地址，注册中心的服务请使用lb://[服务ID]，否则请使用http[s]://[服务地址]"} />

                                        </Form.Item>
                                        <MinusCircleOutlined onClick={() => remove(field.name)}/>
                                    </div>

                                ))}
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined/>}>
                                    新增地址
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Form.Item>
                <Divider />
                <Form.Item label={"Host"} name={"host"}>
                    <Input placeholder={"如果你想使用Host去匹配路由，请填写你希望匹配的Host"} />
                </Form.Item>
                <Form.Item label="Path" name="path" rules={[{required: true, message: "你必须提供路径的匹配规则"}]}>
                    <Input placeholder={"请填写你希望匹配的路径"}/>
                </Form.Item>
                <Divider/>
                <Form.Item label="路径重写">
                    <Checkbox checked={this.state.isEnabledPathRewrite} onChange={
                        (event) => {
                            this.setState({isEnabledPathRewrite: event.target.checked})
                        }
                    }>启用路径重写</Checkbox>
                </Form.Item>
                <Col offset={4} style={{display: this.state.isEnabledPathRewrite ? "inherit" : "none"}}>
                    <Form.Item wrapperCol={{span: 24}} name={"rewriteTarget"}>
                        <Input placeholder={"要重写的目标url"}/>
                    </Form.Item>
                    <Form.Item wrapperCol={{span: 24}} name={"rewriteValue"}>
                        <Input placeholder={"重写的值"}/>
                    </Form.Item>
                </Col>
                <Divider/>
                <Alert message={'熔断在服务故障或者请求延时较高是生效， 如果不启用，我们将会使用默认的熔断配置'} type={"info"} />
                <Form.Item label="熔断配置">
                    <Checkbox checked={this.state.isEnabledFallback} onChange={(event) => {
                        this.setState({
                            isEnabledFallback: event.target.checked
                        })
                    }}>启用自定义熔断配置</Checkbox>
                </Form.Item>
                <div style={{display: this.state.isEnabledFallback ? "inherit" : "none"}}>
                    <Form.Item label={"熔断器类型"} name={"fallbackConfig"}>
                        <Radio.Group>
                            <Tooltip title={"将使用默认的配置熔断配置"}>
                                <Radio.Button value={"default"}>默认配置</Radio.Button>
                            </Tooltip>
                            <Tooltip title={"相对默认配置，增加了请求响应的等待时间和请求失败比例"}>
                                <Radio.Button value={"slow"}>慢响应接口</Radio.Button>
                            </Tooltip>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item label="回退地址" name="fallbackUri">
                        <Input addonBefore="forward:"/>
                    </Form.Item>
                </div>
                <Divider/>
                <Alert message={'是否期望启用失败立即重试，如果启用，我们默认会重试500,502,503的http状态码，如果希望支持其他的状态码的重试，请在下面输入的重试状态码中输入'} type={"info"} />
                <Form.Item label={"重试配置"}>
                    <Checkbox checked={this.state.isEnabledRetry} onChange={(event) => {
                        this.setState({
                            isEnabledRetry: event.target.checked
                        })
                    }}>启用重试配置</Checkbox>
                </Form.Item>
                <div style={{display: this.state.isEnabledRetry ? "inherit" : "none"}}>
                    <Row style={{marginLeft: 10}}>
                        <Col span={8}>
                            <Form.Item label={"重试次数"} name={"retryCount"} labelCol={{span: 11}}
                                       wrapperCol={{span: 13}}>
                                <Input type={"number"} min={1} max={10}/>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label={"最短重试时间"} name={"retryMinTime"} labelCol={{span: 8}}
                                       wrapperCol={{span: 16}}>
                                <Input addonAfter={"ms"}/>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label={"最长重试时间"} name={"retryMaxTime"} labelCol={{span: 8}}
                                       wrapperCol={{span: 16}}>
                                <Input addonAfter={"ms"}/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Tooltip
                        title={"需要进行重试的状态码, 请使用org.springframework.http.HttpStatus枚举值, 多个值使用','分割"}>
                        <Form.Item label={"重试状态码"} name={"retryStatusCode"}>
                            <Input />
                        </Form.Item>
                    </Tooltip>
                    <Form.Item label={"重试方法"} name={"retryMethod"}>
                        <Checkbox.Group>
                            <Checkbox value={"GET"}>GET</Checkbox>
                            <Checkbox value={"POST"}>POST</Checkbox>
                            <Checkbox value={"DELETE"}>DELETE</Checkbox>
                            <Checkbox value={"PUT"}>PUT</Checkbox>
                        </Checkbox.Group>
                    </Form.Item>
                </div>
                <Divider/>
                <Alert message={'请求限流用于服务端的主动流量控制，目前版本网关默认使用路由的id来进行请求限流'} type={"info"} />
                <Form.Item label={"请求限流"}>
                    <Checkbox checked={this.state.isEnabledRateLimiter} onChange={(event) => {
                        this.setState({isEnabledRateLimiter: event.target.checked})
                    }
                    }>启用请求限流</Checkbox>
                </Form.Item>
                <div style={{display: this.state.isEnabledRateLimiter ? "inherit" : "none"}}>
                    <Form.Item label={"限流算法"} name={"rateLimiter"}>
                        <Radio.Group onChange={(event) => {
                            this.setState({
                                selectedRateLimiter: event.target.value
                            })
                        }}>
                            <Radio.Button value={"TokenBucket"}>令牌桶算法</Radio.Button>
                            <Radio.Button value={"LeakyBucket"}>漏桶算法</Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                    <div style={{display: this.state.selectedRateLimiter === "TokenBucket" ? "inherit" : "none"}}>
                        <TokenBucketRateLimiter />
                    </div>
                    <div style={{display: this.state.selectedRateLimiter === "LeakyBucket" ? "inherit" : "none"}}>
                        <LeakyBucketRateLimiter />
                    </div>
                </div>
                <Divider />
                <Form.Item label={"鉴权配置"}>
                    <Checkbox checked={this.state.isEnabledAuth}
                              onChange={(event) => {
                                  this.setState({
                                      isEnabledAuth: event.target.checked
                                  })
                              }}>启用鉴权</Checkbox>
                </Form.Item>
                <div style={{display: this.state.isEnabledAuth ? "inherit" : "none"}}>
                    <Form.Item label={"鉴权跳过路径"}>
                        <Form.List name={"skipPath"}>
                            {
                                (fields, {add, remove}) => (
                                    <>
                                        {
                                            fields.map(field => (
                                                <div style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    marginBottom: '24px'
                                                }}>
                                                    <Form.Item {...field} style={{
                                                        margin: "0 5px 0 0",
                                                        width: '100%'
                                                    }}>
                                                        <Input style={{marginRight: 5}}/>
                                                    </Form.Item>
                                                    <MinusCircleOutlined onClick={() => remove(field.name)}/>
                                                </div>
                                            ))
                                        }
                                        <Form.Item>
                                            <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined/>}>
                                                新增路径
                                            </Button>
                                        </Form.Item>
                                    </>
                                )
                            }
                        </Form.List>
                    </Form.Item>
                    <Form.Item label={"time diff"} >
                        <Input type={"number"} min={0} placeholder={"这是鉴权时用于计算请求是否过期的参数"} />
                    </Form.Item>
                </div>
                <Divider />
                <Form.Item label={"启用日志"}>
                    <Checkbox checked={this.state.isEnabledLogging}
                              onChange={(event) => {
                                  this.setState({
                                      isEnabledLogging: event.target.checked
                                  })
                              }}>启用日志</Checkbox>
                </Form.Item>
                <div  style={{display: this.state.isEnabledLogging ? "inherit" : "none"}}>
                    <Form.Item label={"日志等级"} name={"loggingLevel"}>
                        <Radio.Group onChange={(event) => {this.setState({selectedLoggingLevel: event.target.value})}} >
                            <Tooltip title={"将会详细记录每次请求的数据"}>
                                <Radio.Button value={LoggingLevel.DEBUG}>DEBUG</Radio.Button>
                            </Tooltip>
                            <Tooltip title={'将会记录每次请求的概要数据'}>
                                <Radio.Button value={LoggingLevel.INFO}>INFO</Radio.Button>
                            </Tooltip>
                            <Tooltip title={'将会记录慢相应等日志数据'}>
                                <Radio.Button value={LoggingLevel.WARNING}>WARNING</Radio.Button>
                            </Tooltip>
                            <Tooltip title={"仅记录错误日志"}>
                                <Radio.Button value={LoggingLevel.ERROR}>ERROR</Radio.Button>
                            </Tooltip>
                        </Radio.Group>
                    </Form.Item>
                    <div style={{display: this.state.selectedLoggingLevel === LoggingLevel.WARNING ? "inherit":"none"}}>
                        <Form.Item label={"响应时间阈值"} name={"requestTime"}>
                            <Input />
                        </Form.Item>
                    </div>
                </div>
                <Divider />
                <Alert message={"启用保密配置之后，网关将会过滤其中的敏感信息"} />
                <Form.Item label={"保密配置"}>
                    <Checkbox checked={this.state.isEnabledSecretFilter} onChange={(event) => {
                        this.setState({
                            isEnabledSecretFilter: event.target.checked
                        })
                    }}>启用保密配置</Checkbox>
                </Form.Item>
                <div style={{display: this.state.isEnabledSecretFilter? "inherit" : "none"}}>
                    {this.generateSecretConfig()}
                </div>
            </Form>
        )
    }
}
